import CurrencyFormatter from '../../utils/currency-formatter-lib';

export default class Pricing {
    constructor(parent) {
        this.parent = parent;
        this.client = parent.client;
        this.data = parent.data;
        this.car = parent.car;
        this.mtoPricing = {};
        this.mtocePricing = {};
        this.formatter = new CurrencyFormatter();
    }

    format(val, allowZero, el) {
        const value = val !== null && !isNaN(val) ? Number(val) : NaN;

        if(isNaN(value) || value < 0){
            return el?.dataset?.missingPrice ||
                window.HONDA.CarConfigurator.labels.missingPrice;
        }

        if (!allowZero && value === 0) {
            return el?.dataset?.asStandard ||
                window.HONDA.CarConfigurator.labels.asStandard;
        }

        return this.formatter.to(value);
    }

    async getMTOPricing() {
        if (!this.mtoPricingRequest) {
            const ids = this.data.cars.map((car) => car.mto).filter((c) => c).join(',');
            this.mtoPricingRequest = this.client.getCarPricing(ids);
        }
        return await this.waitForMTOPricing();
    }

    async waitForMTOPricing() {
        return this.mtoPricing = await this.mtoPricingRequest;
    }

    async getMTOCEPricing() {
        const m = this.car.m();
        const mtoce = this.car.mtoce();
        this.mtocePricingRequest = this.client.getCarPricing(mtoce);
        const pricing = await this.waitForMTOCEPricing();
        return pricing;
    }

    async waitForMTOCEPricing() {
        this.mtocePricing = await this.mtocePricingRequest;
        this.carMTOCEPricing = this.getCarMTOCEPricing();
        this.pricingBreakdown = this.getPricingBreakdown();
        return this.mtocePricing;
    }

    hasPricing() {
        return this.mtocePricing && this.carMTOCEPricing;
    }

    getPricingBreakdown() {
        try {
          const carBundle = this.getCarBundle('mtoc');
          return carBundle ? JSON.parse(carBundle.c_priceBreakdown) : [];
        } catch (err) {
            return [];
        }
    }

    getMTOPrices({ discountPrice } = {}) {
        return this.mtoPricing?.data?.reduce((map, mtoPricing) => {
            const ean = mtoPricing['ean'];
            const key = discountPrice ? 'c_priceAfterDiscount' : 'price';
            const price = mtoPricing[key];
            map[ean] = (price != null  && !isNaN(price)) ? Number(price) : null;
            return map;
        }, {}) || {};
    }

    getColourPricesMTO() {
        const data = this.getCarMTOPricing();
        if(data && data['c_colours']) {
            return data['c_colours'].reduce((map, colour) => {
                map[colour.ean] = colour.grossPrice ? Number(colour.grossPrice) : null;
                return map;
            }, {});
        }
        return {};
    }

    getColourPrices() {
        return this.mtoPricing?.data
            .flatMap((p) => p.c_colours)
            .filter((e) => e)
            .reduce((map, colour) => {
                if(map[colour.ean] == null) {
                    map[colour.ean] = colour.grossPrice ? Number(colour.grossPrice) : null;
                }
                return map;
            }, this.getColourPricesMTO()) ?? {}; // Prioritises prices from current MTO by starting the reduce with the current MTO prices
    }

    getTrimPricesMTO() {
        const data = this.getCarMTOPricing();
        if(data && data['c_trims']) {
            return data['c_trims'].reduce((map, trim) => {
                map[trim.ean] = trim.grossPrice ? Number(trim.grossPrice) : null;
                return map;
            }, {});
        }
        return {};
    }

    getTrimPrices() {
        return this.mtoPricing?.data
            .flatMap((p) => p.c_trims)
            .filter((e) => e)
            .reduce((map, trim) => {
                if(map[trim.ean] == null) {
                    map[trim.ean] = trim.grossPrice ? Number(trim.grossPrice) : null;
                }
                return map;
            }, this.getTrimPricesMTO()) ?? {}; // Prioritises prices from current MTO by starting the reduce with the current MTO prices
    }

    getColourTrimPricesMTO({ discountPrice } = {}) {
        const data = this.getCarMTOPricing();
        if(data && data['c_mtocc']) {
            return data['c_mtocc'].reduce((map, mtoc) => {
                const ct = `${mtoc.c_extColourCode}|${mtoc.c_intColourCode}`;
                const key = discountPrice ? 'c_priceAfterDiscount' : 'grossPrice';
                const price = mtoc[key];
                map[ct] = (price != null  && !isNaN(price)) ? Number(price) : null;
                return map;
            }, {});
        }
        return {};
    }

    getColourTrimPrices({ discountPrice } = {}) {
      return this.mtoPricing?.data
          .flatMap((p) => p['c_mtocc'])
          .filter((e) => e)
          .reduce((map, mtoc) => {
            const ct = `${mtoc.c_extColourCode}|${mtoc.c_intColourCode}`;
            const key = discountPrice ? 'c_priceAfterDiscount' : 'grossPrice';
            const price = mtoc[key];
            if(map[ct] == null && !discountPrice) { 
                map[ct] = (price != null  && !isNaN(price)) ? Number(price) : null;
            }
            return map;
          }, this.getColourTrimPricesMTO({ discountPrice })) ?? {}; // Prioritises prices from current MTO by starting the reduce with the current MTO prices
    }

    getAccessoryPricesMTO({ discountPrice } = {}) {
        const data = this.getCarMTOPricing();
        if(data && data['c_euroOptions']) {
            return data['c_euroOptions'].reduce((map, accessory) => {
                const key = discountPrice ? 'c_priceAfterDiscount' : 'grossPrice';
                const price = accessory[key];
                map[accessory.ean] = (price != null  && !isNaN(price)) ? Number(price) : null;
                return map;
            }, {});
        }
        return {};
    }

    getAccessoryPrices({ discountPrice } = {}) {
        return this.mtoPricing?.data
            .flatMap((p) => p.c_euroOptions)
            .filter((e) => e)
            .reduce((map, accessory) => {
                const key = discountPrice ? 'c_priceAfterDiscount' : 'grossPrice';
                const price = accessory[key];
                if(map[accessory.ean] == null && !discountPrice) {
                    map[accessory.ean] = (price != null  && !isNaN(price)) ? Number(price) : null;
                }
                return map;
            }, this.getAccessoryPricesMTO({ discountPrice })) ?? {}; // Prioritises prices from current MTO by starting the reduce with the current MTO prices
    }

    getWheelPrices({ discountPrice } = {}) {
        const defaultPrice = 0.0;
        const data = this.getAccessoryPrices({ discountPrice });
        return new Proxy(data, {
            get(obj, p) {
                const fallback = discountPrice ? null : defaultPrice;
                return obj.hasOwnProperty(p) ? obj[p] : fallback;
            },
        });
    }

    getAdditionalProductsMTO(mto) {
        const data = this.getCarMTOPricing(mto);
        if(data && data['c_additionalProducts']) {
            return data['c_additionalProducts']
                .map((ap) => this.parseAdditionalProductDependencies(ap));
        }
    }

    getAdditionalProductMTO(ean, mto) {
        const additionalProducts =  this.getAdditionalProductsMTO(mto)
        return additionalProducts?.find((ap) => ap.ean === ean);
    }

    getAdditionalProductPricesMTO({ discountPrice } = {}) {
        const data = this.getCarMTOPricing();
        if(data && data['c_additionalProducts']) {
            return data['c_additionalProducts'].reduce((map, product) => {
              const key = discountPrice ? 'c_priceAfterDiscount' : 'grossPrice';
              const price = product[key];
              map[product.ean] = (price != null  && !isNaN(price)) ? Number(price) : null;
              return map;
            }, {});
        }
        return {};
    }

    getCarCapCode() {
      return this.carMTOCEPricing?.c_capCode;
    }

    getCarHoldingFee() {
        return this.carMTOCEPricing?.c_holdingDepositAmount ?? NaN;
    }

    getCarMTOPricing(mto) {
        const target = mto ?? this.car.mto();
        return this.mtoPricing?.data?.find(mtoPricing => target === mtoPricing.ean);
    }

    getCarMTOCEPricing() {
        const mtoce = this.car.mtoce();
        return this.mtocePricing?.data?.find((mtocePricing) => mtoce === mtocePricing.id);
    }

    getCarAdditionalProducts({ tempCar, financeableAdditionalProducts, optionalAdditionalProducts } = {}) {
        const car = tempCar ?? this.car;
        const carMTOPricing = this.getCarMTOPricing(car.mto());
        return car.additionalProducts
            .map((product) => carMTOPricing?.c_additionalProducts?.find((p) => p.ean === product))
            .filter((p) => p)
            .filter((p) => (optionalAdditionalProducts === null || optionalAdditionalProducts === undefined)
                            || (optionalAdditionalProducts === true && !p.additionalProductIncluded)
                            || (optionalAdditionalProducts === false && p.additionalProductIncluded))
            .filter((p) => (financeableAdditionalProducts === null || financeableAdditionalProducts === undefined)
                            || (financeableAdditionalProducts === true && p['type'] !== 'SRV')
                            || (financeableAdditionalProducts === false && p['type'] === 'SRV'))
            .map((p) => this.parseAdditionalProductDependencies(p));
    }

    parseAdditionalProductDependencies(product) {
        const incompatibleWith = product.incompatibileWith;
        const dependsOn = product.c_additionalProductDependency;
        product.cantHave = ((typeof incompatibleWith === 'string' ? JSON.parse(incompatibleWith) : incompatibleWith) ?? []).map((dep) => dep?.ean).filter((dep) => dep);
        product.mustHave = ((typeof dependsOn === 'string' ? JSON.parse(dependsOn) : dependsOn) ?? []).map((dep) => dep?.ean).filter((dep) => dep);
        return product;
    }

    getCarColourTrim() {
        const mtoPricing = this.getCarMTOPricing();
        const colourTrim = this.car.colourTrim();
        return mtoPricing['c_mtocc']?.find((ct) => ct['c_extColourCode'] === colourTrim.colourMTOCE && ct['c_intColourCode'] === colourTrim.trimMTOCE);
    }

    getCarBundle({ type } = {}) {
      const cond = type === 'mtoc' ? (product) => product['c_isMtocc'] === true :
                  type === 'mtoe' ? (product) => product['c_isEopBundle'] === true :
                  (product) => product.name === type;
      return this.carMTOCEPricing?.c_bundledProducts?.find((product) => cond(product));
    }

    getCarBreakdownPrice({ type } = {}) {
        return this.pricingBreakdown?.find((pb) => pb.type === type)?.base ?? NaN;
    }

    getCarBundlePrice({ type, defaultVal, discountPrice } = {}) {
        const carBundle = this.getCarBundle({ type })
        return carBundle?.[discountPrice ? 'c_priceAfterDiscount' : 'basePrice'] ?? defaultVal ?? NaN;
    }

    getCarBreakdownColourPrice() {
        return this.getCarBreakdownPrice({ type: 'extColour' });
    }

    getCarBreakdownTrimPrice() {
        return this.getCarBreakdownPrice({ type: 'intColour' });
    }

    getCarColourTrimPrice({ discountPrice } = {}) {
        if(discountPrice) {
            return this.getCarBreakdownPrice({ type: 'colourPriceAfterDiscount'})            
        } else {
            return this.getCarBreakdownColourPrice() + this.getCarBreakdownTrimPrice();
        }
    }

    getCarAccessoriesPrice({ discountPrice } = {}) {
        return this.getCarBundlePrice({type: 'mtoe', defaultVal: 0, discountPrice });
    }

    getCarAdditionalProductsPrice({ financeableAdditionalProducts, discountPrice, optionalAdditionalProducts }) {
        const additionalProducts = this.getCarAdditionalProducts({ financeableAdditionalProducts, optionalAdditionalProducts });
        const prices = additionalProducts.map((ap) => {
            return {
                price: Number(ap['price'] ?? ap['grossPrice'] ?? NaN),
                discountPrice: Number(ap['c_priceAfterDiscount'] ?? NaN),
            }
        }) ?? [];
        const hasDiscount = prices.some((price) => !isNaN(price.discountPrice));
        if(discountPrice === true && !hasDiscount) { return null; } //If explicit discountPrice is true and no products are discounted return null

        const totalPrice = prices.reduce((total, curr) => total + curr.price, 0);
        if(discountPrice === false) { return totalPrice; } //If expicit discountPrice is false return total price of car without discounts
        const totalDiscountPrice = prices.reduce((total, curr) => total + (!isNaN(curr.discountPrice) ? curr.discountPrice : curr.price), 0);

        return !isNaN(totalDiscountPrice) ? totalDiscountPrice : totalPrice; //Else not explict discount price, return discount price if there is one, else total price
    }

    getCarBasePrice({ financeableAdditionalProducts, discountPrice } = {}) {
        const deliveryCharge = this.getCarBundlePrice({ type: 'deliveryFee' });
        const ved = this.getCarBundlePrice({ type: 'ved' });
        const firstRegistration = this.getCarBundlePrice({ type: 'firstRegistrationFee' });
        const total = this.getCarTotalPrice({ financeableAdditionalProducts, discountPrice });
        return total - deliveryCharge - ved - firstRegistration;
    }

    getCarTotalPrice({ financeableAdditionalProducts, discountPrice } = {}) {
        const mtoce = this.carMTOCEPricing;
        const additionalProducts = this.getCarAdditionalProducts({ financeableAdditionalProducts, optionalAdditionalProducts: true });
        const products = [ mtoce, ...additionalProducts];
        const prices = products.map((ap) => {
            return {
                price: Number(ap['price'] ?? ap['grossPrice'] ?? NaN),
                discountPrice: Number(ap['c_priceAfterDiscount'] ?? NaN),
            }
        }) ?? [];
        
        const hasDiscount = prices.some((price) => !isNaN(price.discountPrice));
        if(discountPrice === true && !hasDiscount) { return null; } //If explicit discountPrice is true and no products are discounted return null

        const totalPrice = prices.reduce((total, curr) => total + curr.price, 0);
        if(discountPrice === false) { return totalPrice; } //If expicit discountPrice is false return total price of car without discounts
        const totalDiscountPrice = prices.reduce((total, curr) => total + (!isNaN(curr.discountPrice) ? curr.discountPrice : curr.price), 0);

        return !isNaN(totalDiscountPrice) ? totalDiscountPrice : totalPrice; //Else not explict discount price, return discount price if there is one, else total price
    }
}
